import React, { useEffect, useState } from 'react'
import { Heart, UserPlus, Users } from 'lucide-react'
import {
  CommunityIntroductionRequestRow,
  CommunityMemberRow,
  fetchAdminUserId,
  fetchIntroducibleMembers,
  fetchMyIntroductionRequests,
  HELP_FIND_CONNECTION_NOTE,
  requestCommunityIntroduction,
  respondToCommunityIntroduction,
} from '../../lib/communityData'
import GlassCard from '../ui/GlassCard'
import GradientButton from '../ui/GradientButton'

/** Sentinel used only in local component state to mark "composing a request
 * addressed to Cassie herself" — never sent to the server as a real id. */
const CASSIE_TARGET = '__cassie__'

interface CommunityMembersPanelProps {
  userId: string
  token: string
}

const statusLabel: Record<CommunityIntroductionRequestRow['status'], string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  declined: 'Declined',
  closed: 'Closed by Cassie',
}

const CommunityMembersPanel: React.FC<CommunityMembersPanelProps> = ({ userId, token }) => {
  const [members, setMembers] = useState<CommunityMemberRow[]>([])
  const [requests, setRequests] = useState<CommunityIntroductionRequestRow[]>([])
  const [adminUserId, setAdminUserId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [requestingId, setRequestingId] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = async () => {
    setLoading(true)
    try {
      const [m, r, adminId] = await Promise.all([
        fetchIntroducibleMembers(userId),
        fetchMyIntroductionRequests(userId),
        fetchAdminUserId(),
      ])
      setMembers(m)
      setRequests(r)
      setAdminUserId(adminId)
    } catch (err) {
      console.error('Failed to load community members:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  const latestRequestWith = (otherUserId: string) =>
    requests.find((r) => r.requesterId === otherUserId || r.recipientId === otherUserId)

  /**
   * Sends an introduction request to a specific recipient — a regular
   * member, or Cassie's own account when `requestingId === CASSIE_TARGET`.
   * Both cases call the exact same server action (request-introduction);
   * a request to Cassie is handled by her existing admin tools, with no
   * new workflow, table, or permission.
   */
  const handleSendRequest = async (recipientId: string, successMessage: string) => {
    setBusy(true)
    setError('')
    try {
      await requestCommunityIntroduction(token, { recipientId, note: note.trim() || undefined })
      setRequestingId(null)
      setNote('')
      setNotice(successMessage)
      await load()
    } catch (err: any) {
      setError(err?.message || 'Could not send that request right now.')
    } finally {
      setBusy(false)
    }
  }

  const handleRespond = async (requestId: string, response: 'accepted' | 'declined') => {
    setBusy(true)
    try {
      await respondToCommunityIntroduction(token, { requestId, response })
      await load()
    } catch (err) {
      console.error('Failed to respond to introduction request:', err)
    } finally {
      setBusy(false)
    }
  }

  const incomingPending = requests.filter((r) => r.recipientId === userId && r.status === 'pending')
  const priorWithCassie = adminUserId ? latestRequestWith(adminUserId) : undefined
  const canRequestCassie =
    !!adminUserId && (!priorWithCassie || priorWithCassie.status === 'declined' || priorWithCassie.status === 'closed')

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {notice && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">{notice}</div>
      )}

      {/* Permanent "Connections Through Cassie" section — always shown, even
          with an empty directory, so members are never left with no way to
          reach out. */}
      <div className="rounded-3xl bg-brand-navy p-6 sm:p-7 text-white">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest-xl text-brand-blush mb-3">
          <Users className="w-4 h-4" />
          Fit by Cassie Community
        </p>
        <h2 className="font-display text-xl sm:text-2xl font-semibold mb-3">Connections Through Cassie</h2>
        <p className="text-sm text-brand-cream/80 mb-5 max-w-2xl">
          Looking for encouragement, accountability, or someone who understands your journey? You can request a
          connection right here. Cassie coordinates introductions with everyone's consent — contact information is
          never shared automatically, and this isn't a space for business solicitation.
        </p>

        <div className="rounded-2xl bg-white/10 p-4 sm:p-5">
          <h3 className="font-semibold mb-1.5 flex items-center gap-2">
            <Heart className="w-4 h-4 text-brand-blush" />
            Want to connect?
          </h3>
          <p className="text-sm text-white/70 mb-3">
            {members.length === 0
              ? "No other members have joined yet — but you don't have to wait. Reach out to Cassie directly, or ask her to help you find a connection as the community grows."
              : 'Reach out to Cassie, or request an introduction to another member below.'}
          </p>

          {requestingId === CASSIE_TARGET ? (
            <div className="flex flex-col gap-2">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note — tell Cassie a bit about what you're looking for"
                rows={4}
                className="form-input text-sm w-full resize-none !bg-white/10 !text-white placeholder:text-white/50 !border-white/20"
              />
              <div className="flex gap-2">
                <GradientButton
                  onClick={() => adminUserId && handleSendRequest(adminUserId, 'Your request was sent to Cassie.')}
                  size="sm"
                  disabled={busy || !adminUserId}
                >
                  Send
                </GradientButton>
                <GradientButton
                  onClick={() => { setRequestingId(null); setNote('') }}
                  size="sm"
                  variant="outline"
                  className="!bg-white/10 !text-white !border-white/30 hover:!bg-white/20 hover:!border-white/50 hover:!text-white"
                >
                  Cancel
                </GradientButton>
              </div>
              <p className="text-xs text-white/50">Reminder: this isn't for business solicitation.</p>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                <GradientButton
                  onClick={() => {
                    if (members.length > 0) {
                      document
                        .getElementById('community-members-list')
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    } else {
                      setRequestingId(CASSIE_TARGET)
                      setNote(HELP_FIND_CONNECTION_NOTE)
                    }
                  }}
                  size="sm"
                  icon={UserPlus}
                >
                  Request a Connection
                </GradientButton>
                <GradientButton
                  onClick={() => { setRequestingId(CASSIE_TARGET); setNote('') }}
                  size="sm"
                  variant="outline"
                  disabled={!canRequestCassie}
                  className="!bg-white/10 !text-white !border-white/30 hover:!bg-white/20 hover:!border-white/50 hover:!text-white disabled:!opacity-50"
                >
                  Connect With Cassie
                </GradientButton>
              </div>
              {priorWithCassie && (
                <p className="text-xs text-white/50 mt-2">
                  {priorWithCassie.status === 'pending'
                    ? 'Your request to Cassie is pending.'
                    : priorWithCassie.status === 'accepted'
                    ? "You're connected with Cassie."
                    : null}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {incomingPending.length > 0 && (
        <GlassCard hover={false}>
          <h2 className="font-display text-lg font-semibold text-ink mb-3">Introduction requests for you</h2>
          <div className="space-y-3">
            {incomingPending.map((req) => (
              <div key={req.id} className="flex items-center justify-between gap-3 pb-3 border-b border-gray-100 last:border-0">
                <div>
                  <p className="text-sm text-ink">Someone in the community would like Cassie to introduce you.</p>
                  {req.note && <p className="text-xs text-gray-500 mt-1">"{req.note}"</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <GradientButton onClick={() => handleRespond(req.id, 'accepted')} size="sm" disabled={busy}>
                    Accept
                  </GradientButton>
                  <GradientButton onClick={() => handleRespond(req.id, 'declined')} size="sm" variant="outline" disabled={busy}>
                    Decline
                  </GradientButton>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}

      <div id="community-members-list" />
      <GlassCard hover={false}>
        <h2 className="font-display text-lg font-semibold text-ink mb-1">Community members</h2>
        <p className="text-sm text-gray-500 mb-4">
          Want to connect with another member for mutual encouragement and support? Request a Connection Through
          Cassie. She coordinates introductions with both members' consent. This isn't a space for business
          solicitation, and contact information is never shared automatically.
        </p>

        {members.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">
            No other members yet — use "Connect With Cassie" above to reach out directly.
          </p>
        ) : (
          <div className="space-y-3">
            {members.map((member) => {
              const prior = latestRequestWith(member.userId)
              const canRequest = !prior || prior.status === 'declined' || prior.status === 'closed'
              const composing = requestingId === member.userId
              return (
                <div key={member.id} className="pb-3 border-b border-gray-100 last:border-0">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-ink">{member.displayName || 'Member'}</p>
                      {member.shortIntro && <p className="text-sm text-gray-500">{member.shortIntro}</p>}
                    </div>
                    {prior && prior.status === 'pending' ? (
                      <span className="text-xs text-gray-400 shrink-0">Request pending</span>
                    ) : prior && prior.status === 'accepted' ? (
                      <span className="text-xs text-green-600 shrink-0">Introduced</span>
                    ) : composing ? null : (
                      <GradientButton
                        onClick={() => setRequestingId(member.userId)}
                        size="sm"
                        variant="outline"
                        icon={UserPlus}
                        disabled={!canRequest}
                      >
                        Request a Connection Through Cassie
                      </GradientButton>
                    )}
                  </div>

                  {composing && (
                    <div className="mt-3 flex flex-col gap-2">
                      <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Optional note — tell Cassie a bit about what you're looking for"
                        rows={4}
                        className="form-input text-sm w-full resize-none"
                      />
                      <div className="flex gap-2">
                        <GradientButton
                          onClick={() => handleSendRequest(member.userId, 'Introduction request sent to Cassie and your match.')}
                          size="sm"
                          disabled={busy}
                        >
                          Send
                        </GradientButton>
                        <GradientButton onClick={() => { setRequestingId(null); setNote('') }} size="sm" variant="outline">
                          Cancel
                        </GradientButton>
                      </div>
                      <p className="text-xs text-gray-400">Reminder: this isn't for business solicitation.</p>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
        {error && <p className="text-xs text-red-600 mt-3">{error}</p>}
      </GlassCard>
    </div>
  )
}

export default CommunityMembersPanel
